import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  UserCheck,
  Monitor,
  Package,
  Save,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import { confidentialAPI } from '../../services/api';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';


const OffboardingStep7 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [stepData, setStepData] = useState({
    exit_interview_completed: false,
    exit_interview_date: '',
    company_assets_returned: false,
    system_access_revoked: false,
    final_payment_processed: false,
    employment_terminated: false,
    final_exit_notes: '',
    assets_checklist: {
      laptop_returned: false,
      mobile_phone_returned: false,
      access_card_returned: false,
      keys_returned: false,
      documents_returned: false,
      other_assets_returned: false
    },
    access_checklist: {
      email_access_revoked: false,
      system_access_revoked: false,
      building_access_revoked: false,
      software_licenses_revoked: false,
      vendor_access_revoked: false
    }
  });

  // Define the 7 steps for offboarding overview
  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 7);
      setStepData(initialData);
    }
  }, [workflow]);

  const handleSave = async () => {
    try {
      setSaving(true);

      const saveData = {
        ...stepData,
        assets_checklist: JSON.stringify(stepData.assets_checklist),
        access_checklist: JSON.stringify(stepData.access_checklist)
      };

      await saveStep(7, saveData);
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAssetsChecklistChange = (item, checked) => {
    setStepData(prev => ({
      ...prev,
      assets_checklist: {
        ...prev.assets_checklist,
        [item]: checked
      }
    }));
  };

  const handleAccessChecklistChange = (item, checked) => {
    setStepData(prev => ({
      ...prev,
      access_checklist: {
        ...prev.access_checklist,
        [item]: checked
      }
    }));
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-6`);
  };

  const handleComplete = async () => {
    try {
      setSaving(true);

      // Mark all final steps as completed
      const finalData = {
        ...stepData,
        exit_interview_completed: true,
        company_assets_returned: true,
        system_access_revoked: true,
        final_payment_processed: true,
        employment_terminated: true,
        assets_checklist: JSON.stringify(stepData.assets_checklist),
        access_checklist: JSON.stringify(stepData.access_checklist)
      };

      const response = await confidentialAPI.updateOffboardingStep(workflowId, 7, finalData);

      if (response.data && response.data.success) {
        toast.success('Offboarding completed successfully!');
        // Navigate back to overview to show completion
        navigate('/admin/employee-lifecycle?tab=offboarding');
      } else {
        throw new Error((response.data && response.data.error) || 'Failed to complete offboarding');
      }
    } catch (error) {
      console.error('Error completing offboarding:', error);
      toast.error('Failed to complete offboarding');
    } finally {
      setSaving(false);
    }
  };

  const isAssetsComplete = () => {
    const checklist = stepData.assets_checklist;
    return checklist.laptop_returned &&
           checklist.mobile_phone_returned &&
           checklist.access_card_returned &&
           checklist.keys_returned &&
           checklist.documents_returned;
  };

  const isAccessComplete = () => {
    const checklist = stepData.access_checklist;
    return checklist.email_access_revoked &&
           checklist.system_access_revoked &&
           checklist.building_access_revoked &&
           checklist.software_licenses_revoked;
  };

  const canCompleteOffboarding = () => {
    return isAssetsComplete() && isAccessComplete() &&
           stepData.exit_interview_completed && stepData.final_payment_processed;
  };


  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 7: Final Exit
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 7 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_7_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={7}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Exit Interview */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Exit Interview</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={stepData.exit_interview_completed}
                  onChange={(e) => setStepData({...stepData, exit_interview_completed: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_7_completed}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Exit Interview Completed</span>
              </label>
            </div>

            {stepData.exit_interview_completed && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Date
                </label>
                <input
                  type="date"
                  value={stepData.exit_interview_date}
                  onChange={(e) => setStepData({...stepData, exit_interview_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  disabled={workflow.step_7_completed}
                />
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Exit Interview Topics</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Reason for leaving</li>
                <li>• Overall experience</li>
                <li>• Management feedback</li>
                <li>• Process improvements</li>
                <li>• Future recommendations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Final Payment */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Final Payment</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={stepData.final_payment_processed}
                  onChange={(e) => setStepData({...stepData, final_payment_processed: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_7_completed}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Final Payment Processed</span>
              </label>
            </div>

            {stepData.final_payment_processed && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center text-green-800 mb-2">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium">Payment disbursed successfully</span>
                </div>
                <p className="text-sm text-green-700">
                  Final salary, leave encashment, and any outstanding payments have been processed.
                </p>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-amber-800 mb-2">Payment Components</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Final month salary</li>
                <li>• Leave encashment</li>
                <li>• Outstanding claims</li>
                <li>• Notice pay (if applicable)</li>
                <li>• Bond refunds (if applicable)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Assets Return */}
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0 gap-2">
          <Package className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate flex-1">Company Assets Return</h2>
          {isAssetsComplete() && (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.laptop_returned}
                onChange={(e) => handleAssetsChecklistChange('laptop_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Laptop</span>
            </div>
            {stepData.assets_checklist.laptop_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.mobile_phone_returned}
                onChange={(e) => handleAssetsChecklistChange('mobile_phone_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Mobile Phone</span>
            </div>
            {stepData.assets_checklist.mobile_phone_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.access_card_returned}
                onChange={(e) => handleAssetsChecklistChange('access_card_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Access Card</span>
            </div>
            {stepData.assets_checklist.access_card_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.keys_returned}
                onChange={(e) => handleAssetsChecklistChange('keys_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Keys</span>
            </div>
            {stepData.assets_checklist.keys_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.documents_returned}
                onChange={(e) => handleAssetsChecklistChange('documents_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Documents</span>
            </div>
            {stepData.assets_checklist.documents_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.assets_checklist.other_assets_returned}
                onChange={(e) => handleAssetsChecklistChange('other_assets_returned', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Other Assets</span>
            </div>
            {stepData.assets_checklist.other_assets_returned && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>
        </div>
      </div>

      {/* Access Revocation */}
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0 gap-2">
          <Monitor className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate flex-1">System Access Revocation</h2>
          {isAccessComplete() && (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.access_checklist.email_access_revoked}
                onChange={(e) => handleAccessChecklistChange('email_access_revoked', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Email Access</span>
            </div>
            {stepData.access_checklist.email_access_revoked && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.access_checklist.system_access_revoked}
                onChange={(e) => handleAccessChecklistChange('system_access_revoked', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">System Access</span>
            </div>
            {stepData.access_checklist.system_access_revoked && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.access_checklist.building_access_revoked}
                onChange={(e) => handleAccessChecklistChange('building_access_revoked', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Building Access</span>
            </div>
            {stepData.access_checklist.building_access_revoked && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.access_checklist.software_licenses_revoked}
                onChange={(e) => handleAccessChecklistChange('software_licenses_revoked', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Software Licenses</span>
            </div>
            {stepData.access_checklist.software_licenses_revoked && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>

          <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={stepData.access_checklist.vendor_access_revoked}
                onChange={(e) => handleAccessChecklistChange('vendor_access_revoked', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Vendor/External Access</span>
            </div>
            {stepData.access_checklist.vendor_access_revoked && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>
        </div>
      </div>

      {/* Final Notes */}
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Final Exit Notes</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={stepData.employment_terminated}
                onChange={(e) => setStepData({...stepData, employment_terminated: e.target.checked})}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                disabled={workflow.step_7_completed}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Employment Officially Terminated</span>
            </label>

            {canCompleteOffboarding() && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center text-green-800 mb-2">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="text-sm font-medium">Ready to complete offboarding</span>
                </div>
                <p className="text-sm text-green-700">
                  All required tasks have been completed. Click "Complete Offboarding" to finalize.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Final Notes
            </label>
            <textarea
              value={stepData.final_exit_notes}
              onChange={(e) => setStepData({...stepData, final_exit_notes: e.target.value})}
              rows={6}
              className="textarea-admin"
              placeholder="Any final notes about the offboarding process, handover details, or follow-up actions..."
              disabled={workflow.step_7_completed}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrev}
          className="btn btn-admin-outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous Step
        </button>

        <div className="flex space-x-3">
          {!workflow.step_7_completed && workflow.workflow_status !== 'withdrawn' && (
            <button
              onClick={() => setShowWithdrawalModal(true)}
              className="btn btn-danger"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Withdraw
            </button>
          )}

          {!workflow.step_7_completed && workflow.workflow_status !== 'withdrawn' ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-admin-secondary"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Progress'}
              </button>

              {canCompleteOffboarding() && (
                <button
                  onClick={handleComplete}
                  disabled={saving}
                  className="btn btn-success"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {saving ? 'Completing...' : 'Complete Offboarding'}
                </button>
              )}
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <div className="flex items-center text-green-800">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Offboarding Completed Successfully</span>
              </div>
            </div>
          )}
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={7}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />
        </div>
      </div>
    </div>
  );
};

export default OffboardingStep7;
